import { type ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Icon, type IconName } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';

interface ChartCardProps {
  title: string;
  iconName?: IconName;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isEmpty: boolean;
  emptyLabel: string;
  children: ReactNode;
  headerRight?: ReactNode;
}

export function ChartCard({
  title,
  iconName,
  isLoading,
  isError,
  onRetry,
  isEmpty,
  emptyLabel,
  children,
  headerRight,
}: ChartCardProps) {
  return (
    <Card as="section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          {iconName === undefined ? null : (
            <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
              <Icon name={iconName} />
            </span>
          )}
          <h2 className="text-sm font-bold text-zooindigo truncate">{title}</h2>
        </div>
        {headerRight === undefined ? null : headerRight}
      </div>
      <ChartCardBody
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        isEmpty={isEmpty}
        emptyLabel={emptyLabel}
      >
        {children}
      </ChartCardBody>
    </Card>
  );
}

interface ChartCardBodyProps {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isEmpty: boolean;
  emptyLabel: string;
  children: ReactNode;
}

function ChartCardBody({
  isLoading,
  isError,
  onRetry,
  isEmpty,
  emptyLabel,
  children,
}: ChartCardBodyProps) {
  if (isLoading === true) {
    return (
      <div className="py-16 flex justify-center">
        <LoadingSpinner className="w-6 h-6" />
      </div>
    );
  }
  if (isError === true) {
    return <QueryErrorState onRetry={onRetry} />;
  }
  if (isEmpty === true) {
    return (
      <p className="text-[13px] text-zoomuted italic py-16 text-center">{emptyLabel}</p>
    );
  }
  return <>{children}</>;
}
